import { useCallback, useEffect, useState } from "react";
import { backend, WalletListItem } from "@/services/backend";
import { useTonConnect } from "./useTonConnect";
import { useCollectionContract } from "./useCollectionContract";
import { useSnackbar } from "notistack";
import { sleep } from "../utils/common";
import { RootState } from "@/state/reducer";
import { useSelector } from "react-redux";

interface UseOwnerWhitelistProps {
  collection: string;
}

export function useOwnerWhitelist({ collection }: UseOwnerWhitelistProps) {
  const snackbar = useSnackbar();
  const { fetchBalance } = useTonConnect();
  const [whitelistItems, setWhitelistItems] = useState<WalletListItem[]>([]);
  const [requestItems, setRequestItems] = useState<WalletListItem[]>([]);
  const [listedItems, setListedItems] = useState<WalletListItem[]>([]);
  const [requestsLoaded, setRequestsLoaded] = useState(0);
  const [listedLoaded, setListedLoaded] = useState(0);
  const { whitelistTrigger } = useSelector((state: RootState) => state);
  const [requestsDisplayed, setRequestsDisplayed] = useState(25);
  const [listedDisplayed, setListedDisplayed] = useState(25);
  const [totalRequestsCount, setTotalRequestsCount] = useState(0);
  const [totalListedCount, setTotalListedCount] = useState(0);
  const { updateWhitelistMultiple, updateWhitelist, calcMassUpdateValue } =
    useCollectionContract();
  const [isLoading, setIsLoading] = useState(false);
  const [showAddSuccessModal, setShowAddSuccessModal] = useState(false);
  const [showRemoveSuccessModal, setShowRemoveSuccessModal] = useState(false);
  const [lastProcessedWallets, setLastProcessedWallets] = useState<string[]>(
    []
  );
  const [lastAction, setLastAction] = useState<"add" | "remove" | null>(null);
  const [searchTerm, setSearchTerm] = useState("");

  const fetchWhitelistData = useCallback(
    async (whitelisted?: boolean, limit = 25, offset = 0, search?: string) => {
      if (!collection) return [];

      try {
        const whitelist = await backend.getCollectionWhitelist({
          collection,
          limit,
          offset,
          whitelisted,
          ...(search?.trim() && { search: search.trim() }),
        });
        return whitelist;
      } catch (error) {
        console.error("Error fetching whitelist:", error);
        return [];
      }
    },
    [collection]
  );

  const fetchTotalCounts = useCallback(async () => {
    if (!collection) return;

    try {
      const [requestsCount, listedCount] = await Promise.all([
        backend.getCollectionWhitelistCount(collection, false),
        backend.getCollectionWhitelistCount(collection, true),
      ]);

      setTotalRequestsCount(requestsCount);
      setTotalListedCount(listedCount);
    } catch (error) {
      console.error("Error fetching total counts:", error);
    }
  }, [collection, whitelistTrigger]);

  const fetchAllWhitelistData = useCallback(
    async (search?: string) => {
      if (!collection) return;

      try {
        const [requests, listed] = await Promise.all([
          fetchWhitelistData(false, 25, 0, search),
          fetchWhitelistData(true, 25, 0, search),
        ]);

        setRequestItems(requests);
        setListedItems(listed);
        setRequestsLoaded(requests.length);
        setListedLoaded(listed.length);
        setRequestsDisplayed(Math.min(25, requests.length));
        setListedDisplayed(Math.min(25, listed.length));

        setWhitelistItems([...requests, ...listed]);

        // Fetch total counts separately (without search filter)
        await fetchTotalCounts();
      } catch (error) {
        console.error("Error fetching whitelist data:", error);
      }
    },
    [collection, fetchWhitelistData, fetchTotalCounts]
  );

  const fetchWhitelist = fetchAllWhitelistData;

  useEffect(() => {
    fetchWhitelist(searchTerm);
  }, [fetchWhitelist, searchTerm]);

  const loadMoreRequests = useCallback(
    async (offset: number) => {
      if (searchTerm.trim()) {
        return 0;
      }

      if (requestsDisplayed < requestItems.length) {
        const newDisplayed = Math.min(
          requestsDisplayed + 25,
          requestItems.length
        );
        setRequestsDisplayed(newDisplayed);
        return newDisplayed - requestsDisplayed;
      }

      const moreRequests = await fetchWhitelistData(false, 25, offset);
      if (moreRequests.length > 0) {
        setRequestItems(prev => [...prev, ...moreRequests]);
        setRequestsLoaded(prev => prev + moreRequests.length);
        setRequestsDisplayed(prev => prev + moreRequests.length);
      }
      return moreRequests.length;
    },
    [fetchWhitelistData, searchTerm, requestsDisplayed, requestItems.length]
  );

  const loadMoreListed = useCallback(
    async (offset: number) => {
      if (searchTerm.trim()) {
        return 0;
      }

      if (listedDisplayed < listedItems.length) {
        const newDisplayed = Math.min(listedDisplayed + 25, listedItems.length);
        setListedDisplayed(newDisplayed);
        return newDisplayed - listedDisplayed;
      }

      const moreListed = await fetchWhitelistData(true, 25, offset);
      if (moreListed.length > 0) {
        setListedItems(prev => [...prev, ...moreListed]);
        setListedLoaded(prev => prev + moreListed.length);
        setListedDisplayed(prev => prev + moreListed.length);
      }
      return moreListed.length;
    },
    [fetchWhitelistData, searchTerm, listedDisplayed, listedItems.length]
  );

  const showLessRequests = useCallback(() => {
    setRequestsDisplayed(25);
  }, []);

  const showLessListed = useCallback(() => {
    setListedDisplayed(25);
  }, []);

  const refreshWhitelistData = useCallback(async () => {
    await fetchAllWhitelistData(searchTerm);
  }, [fetchAllWhitelistData, searchTerm, whitelistTrigger]);

  const balance = useSelector((state: RootState) => state.balance);

  const manageWhitelist = async (wallets: string[], isAdd: boolean) => {
    if (!collection) return;
    const value = Number(calcMassUpdateValue(wallets.length, isAdd));
    console.log("Check balance value:", value);

    // Fetch fresh balance before checking
    /* const currentBalance = await fetchBalance();
    if (!currentBalance || Number(currentBalance) < value) {
      snackbar.enqueueSnackbar("Insufficient balance", {
        variant: "error",
        autoHideDuration: 1000,
      });
      return;
    } */

    try {
      const response =
        wallets.length > 1
          ? await updateWhitelistMultiple(wallets, collection, isAdd)
          : await updateWhitelist(wallets[0], collection, isAdd);
      setIsLoading(true);
      console.log(response, "response");

      if (response) {
        snackbar.enqueueSnackbar("Transaction sent successfully", {
          SnackbarProps: {
            onClick: () => {
              snackbar.closeSnackbar();
            },
          },
          variant: "info",
          autoHideDuration: 2000,
        });

        await sleep(20000);

        try {
          let whitelist = await backend.refreshCollectionWhitelist({
            collection,
            profiles: wallets,
          });
          console.log(whitelist, "whitelist");
          let reindexTest =
            whitelist[whitelist.length - 1].is_whitelisted === isAdd;
          console.log("Reindex test:", reindexTest);

          if (!reindexTest) {
            await sleep(10000);
            whitelist = await backend.refreshCollectionWhitelist({
              collection,
              profiles: wallets,
            });
          }
          // Try #2
          reindexTest =
            whitelist[whitelist.length - 1].is_whitelisted === isAdd;
          console.log("Reindex test:", reindexTest);

          if (!reindexTest) {
            await sleep(10000);
            whitelist = await backend.refreshCollectionWhitelist({
              collection,
              profiles: wallets,
            });
          }

          setLastProcessedWallets(wallets);
          setLastAction(isAdd ? "add" : "remove");

          if (isAdd) {
            setShowAddSuccessModal(true);
          } else {
            setShowRemoveSuccessModal(true);
          }

          fetchWhitelist();
        } catch (refreshError) {
          console.error("Error refreshing whitelist:", refreshError);
          snackbar.enqueueSnackbar(
            "Error verifying transaction. Please refresh manually.",
            {
              SnackbarProps: {
                onClick: () => {
                  snackbar.closeSnackbar();
                },
              },
              variant: "warning",
              autoHideDuration: 3000,
            }
          );
        }
      } else {
        snackbar.enqueueSnackbar("Transaction failed to send", {
          SnackbarProps: {
            onClick: () => {
              snackbar.closeSnackbar();
            },
          },
          variant: "error",
          autoHideDuration: 2000,
        });
      }
    } catch (error: any) {
      console.error(`Error ${isAdd ? "adding" : "removing"} whitelist:`, error);

      if (error?.message?.includes("cancelled by user")) {
        snackbar.enqueueSnackbar("Transaction was cancelled", {
          SnackbarProps: {
            onClick: () => {
              snackbar.closeSnackbar();
            },
          },
          variant: "info",
          autoHideDuration: 2000,
        });
      } else {
        snackbar.enqueueSnackbar("Transaction failed", {
          SnackbarProps: {
            onClick: () => {
              snackbar.closeSnackbar();
            },
          },
          variant: "error",
          autoHideDuration: 2000,
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const closeAddSuccessModal = () => {
    setShowAddSuccessModal(false);
    setLastProcessedWallets([]);
    setLastAction(null);
    refreshWhitelistData();
  };

  const closeRemoveSuccessModal = () => {
    setShowRemoveSuccessModal(false);
    setLastProcessedWallets([]);
    setLastAction(null);
    refreshWhitelistData();
  };

  return {
    whitelistItems,
    requestItems: requestItems.slice(0, requestsDisplayed),
    listedItems: listedItems.slice(0, listedDisplayed),
    totalRequestsCount,
    totalListedCount,
    requestsCanLoadMore:
      requestsDisplayed < requestItems.length ||
      (requestItems.length % 25 === 0 && requestItems.length >= 25),
    listedCanLoadMore:
      listedDisplayed < listedItems.length ||
      (listedItems.length % 25 === 0 && listedItems.length >= 25),
    requestsCanShowLess: requestsDisplayed > 25,
    listedCanShowLess: listedDisplayed > 25,
    manageWhitelist,
    isLoading,
    showAddSuccessModal,
    showRemoveSuccessModal,
    lastProcessedWallets,
    closeAddSuccessModal,
    closeRemoveSuccessModal,
    loadMoreRequests,
    loadMoreListed,
    showLessRequests,
    showLessListed,
    refreshWhitelistData,
    fetchWhitelistData,
    searchTerm,
    setSearchTerm,
  };
}
